import { ReactNode } from 'react';
import cn from 'classnames';
import urljoin from 'url-join';
import { HiOutlineExternalLink } from 'react-icons/hi';

import Link from '@/components/renderers/Link';
import ActiveLink from '@/components/ActiveLink';
import * as sidebarConfig from '@/docs-sidebar.config.json';
import { isExternalUrl } from '@/utils/isExternalUrl';
import Website from './icons/Website';
import APIReference from './icons/APIReference';
import Guides from './icons/Guides';
import UIExtensions from './icons/UIExtensions';
import Quickstarts from './icons/Quickstarts';
import { Theme, backgroundTheme } from './docs-card';

interface NavItemProps {
  href: string;
  isActive: boolean;
  title: string;
  children: ReactNode;
  theme: Theme;
}

function NavItem({ href, isActive, title, children, theme }: NavItemProps) {
  return (
    <li>
      <Link
        href={href}
        className={cn(
          `flex items-center transition-colors duration-150 text-sm`,
          {
            'text-gray-900 font-medium': isActive,
            'text-coolGray-500 hover:text-gray-900': !isActive,
          },
        )}
      >
        <div
          className={cn(
            `w-7 h-7 mr-3 rounded-md text-white flex items-center justify-center bg-gradient-to-br`,
            backgroundTheme[theme],
          )}
        >
          {children}
        </div>

        {title}
      </Link>
    </li>
  );
}

interface SidebarProps {
  menuOpen: boolean;
  category: string;
}

export default function Sidebar({ menuOpen, category }: SidebarProps) {
  const activeNav = (sidebarConfig as any)[category];

  return (
    <aside
      className={cn(
        `bg-white flex-shrink-0 h-full md:sticky top-20 bottom-0 md:overflow-y-visible md:w-64`,
        {
          'block md:block': menuOpen,
          'hidden md:block': !menuOpen,
        },
      )}
    >
      <div className="h-full overflow-y-auto md:sticky md:overflow-hidden">
        <div className="md:max-h-[calc(100vh-80px)] p-6 md:p-8 md:overflow-y-auto border-b border-athens-gray md:border-b-0">
          <div className="space-y-8">
            <ul className="space-y-3 border-b border-athens-gray pb-8 text-gray-500">
              <NavItem
                href="/"
                isActive={false}
                title="graphcms.com"
                theme="DEFAULT"
              >
                <Website />
              </NavItem>

              <NavItem
                href="/docs"
                isActive={category === `api-reference`}
                title="API Reference"
                theme="CORNFLOWER"
              >
                <APIReference />
              </NavItem>

              <NavItem
                href="/docs/guides"
                isActive={category === `guides`}
                title="User Guides"
                theme="GREEN"
              >
                <Guides />
              </NavItem>

              <NavItem
                href="/docs/ui-extensions"
                isActive={category === `ui-extensions`}
                title="UI Extensions"
                theme="RED"
              >
                <UIExtensions />
              </NavItem>

              <NavItem
                href="https://github.com/graphcms/graphcms-examples"
                isActive={category === `quickstarts`}
                title="Quickstarts"
                theme="YELLOW"
              >
                <Quickstarts />
              </NavItem>
            </ul>

            {activeNav?.map((cat: any) => (
              <div key={cat.slug} className="space-y-2">
                <h3 className="font-semibold uppercase text-haiti text-xs tracking-wider">
                  {cat.name}
                </h3>

                <ul>
                  {cat.pages.map((page: any) => {
                    const linkClass = `relative py-2 leading-4 w-full hover:text-gray-900 !transition-colors !duration-150 text-sm inline-flex items-center text-coolGray-500 group`;
                    const isLinkExternal = isExternalUrl(page.slug);

                    return (
                      <li key={page.slug} className="mb-[2px]">
                        {isLinkExternal ? (
                          <a
                            href={page.slug}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(linkClass, `flex items-center`)}
                          >
                            {page.name}
                            <HiOutlineExternalLink className="w-4 h-4 ml-2" />
                          </a>
                        ) : (
                          <ActiveLink
                            href={urljoin(
                              `/docs`,
                              category,
                              cat.slug,
                              page.slug,
                            )}
                            activeClassName="!text-cornflower before:bg-faded-cornflower before:absolute before:inset-0 before:rounded before:z-[-1] before:w-[calc(100%+8px)] before:-left-2 before:p-2"
                          >
                            <a className={linkClass}>
                              <span>{page.name}</span>
                            </a>
                          </ActiveLink>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}
